import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form } from '@unform/web';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { FormHandles } from '@unform/core';
import { useNavigate } from 'react-router-dom';

import AppError from 'utils/AppError';
import api from 'services/api';
import { show } from 'store/modules/dialog/actions';
import { updateCurrentDate } from 'store/modules/usuario/actions';

import Input from 'components/Input';
import Card from 'components/Card';
import Tooltip from 'components/Tooltip';
import Flag from 'components/Flag';
import Button from 'components/Button';
import Loader from 'components/ContentLoader/ComerciaisNaoGravadosLoader';
import Checkbox from 'components/Checkbox';

import {
  FiSearch,
  FiVideoOff,
  FiTrash2,
  FiActivity,
  FiCheckSquare,
  FiSquare,
  FiMinusSquare,
} from 'react-icons/fi';

import { Scroll, Td, Align } from 'styles/global';
import { Container, Table, TdFlag } from './styles';
import { Comercial } from 'pages/@types';

export default function ComerciaisIndisponiveis() {
  // react-router-dom
  const navigate = useNavigate();
  /**
   * REDUX
   * */
  const dispatch = useDispatch();
  const { emissora } = useSelector((state: any) => state.usuario);
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );

  /*
   * USE STATE
   */
  const [selected, setSelected] = useState({} as Comercial);
  const [refreshPage, setRefreshPage] = useState(0);
  const [filtro, setFiltro] = useState('');
  const [loading, setLoading] = useState(true);
  const [
    comerciaisIndisponiveisCompleto,
    setComerciaisIndisponiveisCompleto,
  ] = useState([] as Comercial[]);
  const [multipleSelect, setMultipleSelect] = useState([] as number[]);

  const comerciaisIndisponiveis = comerciaisIndisponiveisCompleto.filter(
    c =>
      c.titulo.includes(filtro.toUpperCase()) ||
      c.codmaterial.toString().includes(filtro) ||
      c.cliente.mnemonico.includes(filtro.toUpperCase()),
  );
  /**
   * REFS
   */
  const formTableRef = useRef<FormHandles>(null);

  /**
   * USE CALLBACK
   */
  const handleSelectEnvio = useCallback(
    c => {
      if (multipleSelect.includes(c.id)) {
        setMultipleSelect(multipleSelect.filter(x => x !== c.id));
        if (formTableRef && formTableRef.current)
          formTableRef.current.clearField(`checkbox_${c.id}`);
      } else {
        if (formTableRef && formTableRef.current)
          formTableRef.current.setFieldValue(`checkbox_${c.id}`, [
            { id: c.id, label: '', checked: true },
          ]);

        setMultipleSelect([...multipleSelect, c.id]);
      }
    },
    [multipleSelect],
  );

  const handleSelectAll = useCallback(
    (event, select) => {
      event.preventDefault();
      if (select) {
        setMultipleSelect(comerciaisIndisponiveis.map(c => c.id));
        comerciaisIndisponiveis.forEach(c => {
          if (formTableRef && formTableRef.current)
            formTableRef.current.setFieldValue(`checkbox_${c.id}`, [
              { id: c.id, label: '', checked: true },
            ]);
        });
      } else {
        setMultipleSelect([]);
        comerciaisIndisponiveis.forEach(c => {
          if (formTableRef && formTableRef.current)
            formTableRef.current.clearField(`checkbox_${c.id}`);
        });
      }
    },
    [comerciaisIndisponiveis],
  );

  const handleArquivaMultiplo = useCallback(async () => {
    const comerciaisSelecionados = comerciaisIndisponiveis.filter(c =>
      multipleSelect.includes(c.id),
    );

    const optionBase = {
      type: 'question',
      text: `Deseja deletar todos os ${multipleSelect.length}  envios selecionados?`,
      confirmButtonText: 'CONFIRMAR',
      callback: {
        url: `/enviaexibidor/${comerciaisSelecionados
          .map(c => c.enviaexibidor[0].id)
          .join(',')}`,
        method: api.delete,
        action: 'arquivaMultiplo',
      },
      haveCallback: true,
      successMessage: 'Envios deletados!',
      cancelButtonText: 'NÃO',
      showCancelButton: false,
      showConfirmButton: true,
      showCloseButton: true,
    };
    dispatch(show(optionBase));
  }, [comerciaisIndisponiveis, dispatch, multipleSelect]);

  const checkAllComponent = useCallback(() => {
    if (multipleSelect.length === 0) {
      return (
        <Button
          fill
          color="font"
          round
          style={{
            top: '-3px',
            position: 'absolute',
            left: '-9px',
          }}
          disabled={false}
          onClick={e => handleSelectAll(e, true)}
        >
          <FiSquare size={26} />
        </Button>
      );
    }

    if (
      multipleSelect.length > 0 &&
      multipleSelect.length < comerciaisIndisponiveis.length
    ) {
      return (
        <Button
          fill
          color="font"
          round
          style={{
            top: '-3px',
            position: 'absolute',
            left: '-9px',
          }}
          disabled={false}
          onClick={e => handleSelectAll(e, false)}
        >
          <FiMinusSquare size={26} />
        </Button>
      );
    }

    if (multipleSelect.length <= comerciaisIndisponiveis.length) {
      return (
        <Button
          fill
          color="font"
          round
          style={{
            top: '-3px',
            position: 'absolute',
            left: '-9px',
          }}
          disabled={false}
          onClick={e => handleSelectAll(e, false)}
        >
          <FiCheckSquare size={26} />
        </Button>
      );
    }
  }, [handleSelectAll, multipleSelect.length, comerciaisIndisponiveis.length]);

  const handleGoToComerciaisNaoGravados = useCallback(
    (data: string) => {
      dispatch(updateCurrentDate(new Date(data)));
      navigate('/dashboard/comerciaisnaogravados');
    },
    [dispatch],
  );

  const handleArquiva = async (e: React.MouseEvent, c: Comercial) => {
    e.preventDefault();
    setSelected(c);
    try {
      const option = {
        type: 'question',
        text: `Deseja deletar o envio de ${c.titulo}?`,
        confirmButtonText: 'CONFIRMAR',
        callback: {
          url: `/enviaexibidor/${c.enviaexibidor[0].id}`,
          method: api.delete,
          action: 'arquiva',
        },
        haveCallback: true,
        successMessage: 'Envio deletado!',
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: true,
        showCloseButton: true,
      };
      dispatch(show(option));
    } catch (err) {}
  };
  // const handleBusca = useCallback(
  //   e => {
  //     const filteredIndisponiveis = comerciaisIndisponiveisCompleto.filter(
  //       c =>
  //         c.titulo.includes(e.target.value.toUpperCase()) ||
  //         c.codmaterial.toString().includes(e.target.value) ||
  //         c.cliente.mnemonico.includes(e.target.value.toUpperCase()),
  //     );
  //     setComerciaisIndisponiveis(filteredIndisponiveis);
  //   },
  //   [comerciaisIndisponiveisCompleto],
  // );
  /**
   * USE EFFECT
   */
  useEffect(() => {
    const controller = new AbortController();
    async function loadSemPlanilha() {
      setLoading(true);
      try {
        const response = await api.get(
          `/comerciaists/comerciaisindisponiveis`,
          {
            signal: controller.signal,
            params: { emissora_id: emissora.value },
          },
        );

        if (response && response.data) {
          setComerciaisIndisponiveisCompleto(response.data);
        }
        setMultipleSelect([]);
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
      }
      setLoading(false);
    }
    loadSemPlanilha();
    return () => {
      controller.abort();
    };
  }, [emissora.value, refreshPage]);

  // verifica se callback foi realizado com sucesso para então remover item da lista
  useEffect(() => {
    if (callbackDialog && callbackDialog.success)
      if (
        (callbackDialog.success.action === 'autoriza' ||
          callbackDialog.success.action === 'arquiva') &&
        selected
      ) {
        setMultipleSelect([]);
      } else if (callbackDialog.success.action === 'arquivaMultiplo') {
        setMultipleSelect([]);
        setRefreshPage(prevState => prevState + 1);
      }
  }, [callbackDialog, selected]);

  return (
    <Container>
      <header>
        <Button
          color="primary"
          disabled={multipleSelect.length < 1}
          onClick={() => handleArquivaMultiplo()}
        >
          ARQUIVAR SELECIONADOS <FiTrash2 />
        </Button>
      </header>
      <Card>
        <header>
          <h4>COMERCIAIS INDISPONIVEIS</h4>
          <div>
            <Form onSubmit={() => {}}>
              <Input
                name="searchLocal"
                placeholder="Buscar na Tabela"
                icon={<FiSearch />}
                onChange={e => setFiltro(e.target.value)}
              />
            </Form>
          </div>
        </header>
        {loading ? (
          <Loader />
        ) : (
          <Scroll>
            <Form ref={formTableRef} onSubmit={() => {}}>
              <Table>
                <thead>
                  <tr>
                    <th style={{ width: '15px' }} />
                    <th style={{ position: 'relative', width: '63px' }}>
                      {checkAllComponent()}
                    </th>
                    <th style={{ width: '90px' }}>CM</th>
                    <th>TITULO</th>
                    <th style={{ width: '90px' }}>DUR</th>
                    <th style={{ width: '160px' }}>STATUS</th>
                    <th style={{ width: '150px', textAlign: 'center' }}>
                      RECEBIDO EM
                    </th>
                    <th style={{ width: '90px' }}>AÇÕES</th>
                  </tr>
                </thead>

                <tbody>
                  {!!comerciaisIndisponiveis &&
                    comerciaisIndisponiveis.map((c, index) => (
                      <tr
                        key={c.id}
                        onClick={e => {
                          e.preventDefault();
                          handleSelectEnvio(c);
                        }}
                      >
                        <TdFlag $isGe={c.globoexpress}>
                          <div id={`tdFlag_id${index}`} />
                          <Tooltip
                            target={`tdFlag_id${index}`}
                            position="right"
                          >
                            <p>
                              <strong>PRIORITÁRIO:</strong> GLOBO EXPRESS
                            </p>
                          </Tooltip>
                        </TdFlag>
                        <Td>
                          <Checkbox
                            key={c.id}
                            name={`checkbox_${c.id}`}
                            options={[{ id: c.id, label: '' }]}
                            columns="1fr"
                          />
                        </Td>
                        <Td>{c.codmaterial}</Td>
                        <Td>
                          {c.titulo} <strong>({c.cliente.mnemonico})</strong>
                        </Td>
                        <Td>{c.duracao} s</Td>
                        <Td>
                          <Flag
                            type={c.enviaexibidor[0].statusgeral.type}
                            name={c.enviaexibidor[0].statusgeral.nome}
                            tooltip={c.enviaexibidor[0].statusgeral.info}
                          />
                        </Td>

                        <Td style={{ textAlign: 'center' }}>
                          {format(
                            new Date(c.enviaexibidor[0].created_at),
                            'dd/MM/yyyy',
                          )}
                        </Td>
                        <Td>
                          <Align>
                            <Button
                              id={`historico_${c.id}`}
                              fill
                              color="font"
                              round
                              href={`/historico/${c.id}`}
                            >
                              <FiActivity />
                            </Button>
                            <Tooltip target={`historico_${c.id}`}>
                              IR PARA HISTÓRICO
                            </Tooltip>
                            <Button
                              id={`naogravados_${c.id}`}
                              fill
                              color="font"
                              round
                              onClick={() =>
                                handleGoToComerciaisNaoGravados(
                                  c.enviaexibidor[0].created_at,
                                )
                              }
                            >
                              <FiVideoOff />
                            </Button>
                            <Tooltip target={`naogravados_${c.id}`}>
                              IR PARA NÃO GRAVADOS
                            </Tooltip>
                            <Button
                              id={`arquiva_${c.id}`}
                              fill
                              color="font"
                              round
                              onClick={e => handleArquiva(e, c)}
                            >
                              <FiTrash2 />
                            </Button>
                            <Tooltip target={`arquiva_${c.id}`}>
                              ARQUIVAR
                            </Tooltip>
                          </Align>
                        </Td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </Form>
          </Scroll>
        )}
      </Card>
    </Container>
  );
}
